import { environment } from 'src/environments/environment';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { User } from '../models/user.model';
import { StorageService } from './storage.service';
import { APIResult } from '../models/APIResult.model';
import { ApiService } from './api.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { ShowService } from '../app/show.service';
import { AuthStateService } from '../app/auth/auth-state.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    private readonly showService: ShowService = inject(ShowService);
    private readonly authStateService: AuthStateService = inject(AuthStateService);

    private _currentActiveUser: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly currentActiveUser: Observable<User> = this._currentActiveUser.asObservable();
    private readonly storageService: StorageService = inject(StorageService);
    private readonly userIdsForTesting = 6;

    private readonly api: ApiService = inject(ApiService);
    private readonly http: HttpClient = inject(HttpClient);

    // private _activeUser: User | null = null;

    private storedUser: User | null = null;

    // Cache for getAllUsers to avoid repeated API calls on dashboard
    private allUsersCache: Map<number, User[]> = new Map();
    private allUsersPendingRequests: Map<number, Promise<User[]>> = new Map();

    public async getCurrentUser(): Promise<User> {
        return (await this.storageService.getLocalData('user')) as User;
    }

    /**
     * Clears the cached user data. Should be called when user data might have changed
     * on the server (e.g., after switching active show).
     */
    public clearUserCache(): void {
        this.storedUser = null;
    }

    public async getActiveUser(): Promise<User | null> {

        const activeUserId = await this.getActiveUserId();
        if (activeUserId) {
            const userMaybe = await this.getUser(activeUserId);
            this._currentActiveUser.next(userMaybe);
            return userMaybe;
        }
        console.warn('No active user found. This should not happen when the user is logged in');
        return null;

    }

    /**
     * @deprecated The method should not be used
     */
    // public async setActiveUserId(userId: number): Promise<void> {
    //   await this.storageService.setItem('userId', userId);
    // }

    /**
     * @deprecated The method should not be used
     */
    // public async getActiveUserId_OLD(): Promise<number> {
    //   if (await this.storageService.getItem('userId')) {
    //     return (await this.storageService.getItem('userId')) as number;
    //   }
    //   return null;
    // }

    /**
     * Get all active users for the current show.
     * Results are cached to avoid repeated API calls (e.g., from multiple components).
     */
    public async getAllUsers(): Promise<User[]> {
        const showId = await this.showService.getActiveShowId();

        if (showId === null) {
            console.warn('[UserService] getAllUsers - no active showId');
            return [];
        }

        // Return cached result if available
        if (this.allUsersCache.has(showId)) {
            return this.allUsersCache.get(showId);
        }

        // If a request is already pending for this showId, wait for it
        if (this.allUsersPendingRequests.has(showId)) {
            return this.allUsersPendingRequests.get(showId);
        }

        // Create the request and store it to prevent duplicate calls
        const request = this.fetchAllUsers(showId);
        this.allUsersPendingRequests.set(showId, request);

        try {
            const result = await request;
            this.allUsersCache.set(showId, result);
            return result;
        } finally {
            this.allUsersPendingRequests.delete(showId);
        }
    }

    private async fetchAllUsers(showId: number): Promise<User[]> {
        try {
            const users: User[] = await this.api.getAll<User>(`users?showId=${ showId }`);

            const usersList: User[] = [];

            users.forEach((user => {
                usersList.push(new User().createFromJson(user));
            }));

            usersList.sort((a, b) => (a.firstName > b.firstName ? 1 : -1));

            return usersList.filter(user => user.status === 'ACTIVE');
        } catch (error) {
            console.error('Unable to find users. Error: ', error);
            throw error;
        }
    }

    /**
     * Clear the users cache (e.g., when switching shows or when user data changes).
     */
    public clearUsersCache(showId?: number): void {
        if (showId !== undefined) {
            this.allUsersCache.delete(showId);
        } else {
            this.allUsersCache.clear();
        }
    }

    /**
     * Retrieves user data by user ID. If the user data is already stored in memory, it returns the stored value.
     * Otherwise, it fetches the user data from the API, processes it, and stores it for subsequent calls.
     *
     * @param userId - The ID of the user to be retrieved.
     * @return A promise that resolves to a User object representing the retrieved user data.
     * @throws {Error} If no user ID is provided or if there is an issue retrieving the user data from the API.
     */
    public async getUser(userId: number): Promise<User> {

        if (!userId) {
            throw new Error('No user id provided');
        }

        if (this.storedUser && this.storedUser.id === userId) {
            return this.storedUser;
        }

        try {
            const user = await this.http
                .get<APIResult<User>>(`${ environment.apiUrl }/users/${ userId }`)
                .toPromise();

            const userObject = new User().createFromJson(user.data[0]);

            this.storedUser = userObject;
            return userObject;
        } catch (error) {
            console.error(`Unable to find user with id ${ userId }`);
            throw error;
        }
    }

    public async whoAmI(): Promise<any> {
        try {
            const calendarTokenResponse = await this.api
                .get<APIResult<any>>(`users/whoami`);

            return calendarTokenResponse;

        } catch (error) {
            console.error(`Unable to find myself`);
            throw error;
        }
    }

    /**
     * @deprecated The method should not be used
     */
    // public fetchUsers(): User[] {
    //   const fetchedUsers = this.http.get<any>(`${ environment.apiUrl }/users`);
    //
    //   const users: User[] = [];
    //
    //   fetchedUsers.subscribe((data) => {
    //     data.data.forEach((user) => {
    //       users.push(
    //         User.create(
    //           user.id,
    //           user.username,
    //           user.firstName,
    //           user.lastName,
    //           user.email,
    //           user.rol_id
    //         )
    //       );
    //     });
    //   });
    //
    //   return users;
    // }

    /**
     * @deprecated The method should not be used
     */
    // public fetchSingleUser(id: number): User {
    //   // Get a single user from the API
    //   const fetchedUser = this.http.get<any>(`${ environment.apiUrl }/users/${ id }`);
    //
    //   const user: User = new User();
    //
    //   // Convert fetchedUser into a User object
    //   fetchedUser.subscribe((data) => {
    //     user.setUsername(data.username);
    //     user.setVoornaam(data.voornaam);
    //     user.setAchternaam(data.achternaam);
    //     user.setEmail(data.email);
    //     user.setRolId(data.rol_id);
    //     user.activeShow = data.activeShow;
    //   });
    //
    //   return user;
    // }

    /**
     * @deprecated The method should not be used
     */
    // public async getActiveShowIdForUser(): Promise<number> {
    //   // TODO: Maak dit werkend
    //   return (this.userIdsForTesting === await this.getActiveUserId()) ? 2 : 1;
    // }

    public async getActiveUserToken(): Promise<string> {
        if (await this.storageService.getItem('accessToken')) {
            return (await this.storageService.getItem('accessToken')) as string;
        }
        return null;
    }

    /**
     * Returns the current user's ID from the authenticated state.
     * Uses the validated user data from AuthStateService instead of decoding
     * the JWT token client-side (which would be insecure as it bypasses
     * signature validation).
     */
    public async getActiveUserId(): Promise<number> {
        return this.authStateService.userId;
    }

    public async updateUser(currentUser: User) {
        try {
            const response = await this.api.put<User>(`users/${ currentUser.id }/email`, currentUser);
            return response;
        } catch (e) {
            console.warn(e);
            throw e;
        }
    }

}
